import { Component, OnDestroy, OnInit, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { DateTimePickerComponent } from '@progress/kendo-angular-dateinputs';
import { ComboBoxComponent } from '@progress/kendo-angular-dropdowns';
import { GridComponent } from '@progress/kendo-angular-grid';
import { ComponentBase } from '~/infrastructure/component-base';
import { UserSummary } from '~/models/administration/user';
import {
  StockAdjustCycleCountDetailsModel,
  StockCycleCountChunkDetailModel,
  StockCycleCountChunkModel
} from '~/models/modules/Stocks/Transactions/StockCycleCount/StockCycleCount';
import { FormatService } from '~/services/administration.ts/format';
import { LocationsService } from '~/services/modules/stocks/locations';
import { StockCycleCountAdjustService } from '~/services/modules/stocks/stock-cycle-count-adjust.service';
import { TransactionDateEditingService } from '~/services/modules/stocks/administration/transaction-date-editing';
import { SecurityService } from '~/services/security.service';
import { SharedService } from '~/services/sharedService';
import { ThemeManagerService } from '~/services/theme-manager';
import { LayoutManagerService } from '~/shared/components/layout/layout-manager';
import { LayoutComponent } from '~/shared/components/layout/layout.component';
import { PageState } from '~/shared/enums/stock-files.enum';
import { PageNames } from '~/models/pageNames';

@Component({
    selector: 'app-stock-cycle-count-adjust',
    templateUrl: './stock-cycle-count-adjust.html',
    styleUrls: ['./stock-cycle-count-adjust.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class StockCycleCountAdjustComponent extends ComponentBase implements OnInit, OnDestroy {
  page: string = PageNames.StockCycleCountAdjust;
  title: string = 'Stock Cycle Count - Adjust';
  showTextBox: boolean = false;
  @ViewChild('kendo_Start_Date_Time_Piker') kendoStartDateTimePiker: DateTimePickerComponent;
  @ViewChild('kendo_combo_accounts') kendoComboAccounts: ComboBoxComponent;
  @ViewChild('kendo_combo_sites') kendoComboSites: ComboBoxComponent;
  @ViewChild('grid') grid: GridComponent;

  _exportFileName: string = 'stock-cycle-count-adjust';
  _allSelected: boolean = false;
  _currentUser: UserSummary;
  _theme: string;
  _form: FormGroup;
  _invalidForm: boolean = false;
  _addClassActiveLocation: boolean = false;
  _addClassActiveGrid: boolean = false;
  _addClassActive3: boolean = false;
  _selectedLocations: any[] = [];
  _locations: any[] = [];
  _locationsFiltered: any[] = [];
  _datePickerFormat: string;
  _costFormat: string;
  _dateEnabled: boolean = false;
  loggedInSiteId: number = 0;
  _pageState: PageState;
  public _cycleCountChunksDetails: StockCycleCountChunkDetailModel[] = [];
  public _selectedKeys: any[] = [];
  selectBy = (args: any) => args.dataItem?.id ?? args.index;
  _cycleCountChunks: StockCycleCountChunkModel[] = [];
  _cycleCountChunksFiltered: StockCycleCountChunkModel[] = [];

  _pdfColumns: any[] = [
    { field: 'stockItemCode', name: 'Item Code', width: 200 },
    { field: 'stockItemDescription', name: 'Description', width: 250 },
    { field: 'inventoryNumber', name: 'Inventory #', width: 200 },
    { field: 'expectedLocationName', name: 'Expected Location', width: 200 },
    { field: 'actualLocationName', name: 'Actual Location', width: 200 },
    { field: 'expectedQuantity', name: 'Expected Qty', width: 150 },
    { field: 'actualQuantity', name: 'Actual Qty', width: 150 },
    { field: 'varianceQuantity', name: 'Variance', width: 120 }
  ];

  constructor(
    private _fb: FormBuilder,
    private _themeManager: ThemeManagerService,
    private _layoutComponent: LayoutComponent,
    private _layoutManager: LayoutManagerService,
    private _securityService: SecurityService,
    private _locationsService: LocationsService,
    private _formatService: FormatService,
    private _TransactionDateEditing: TransactionDateEditingService,
    private _stockCycleCountAdjustService: StockCycleCountAdjustService,
    private _sharedService: SharedService,
    private router: Router
  ) {
    super();
    this._themeManager.themeChange.subscribe((data) => {
      this._theme = data;
    });
    this._currentUser = this._securityService.currentUser;
  }

  ngOnInit(): void {
    this._initForm();
    const loggedInAccountSites = localStorage.getItem('loggedInAccountSites') ?? '';
    if (loggedInAccountSites) {
      try {
        this.loggedInSiteId = JSON.parse(loggedInAccountSites)[0]?.siteId || 0;
      } catch (e) {
        this.loggedInSiteId = 0;
      }
    }
    this._datePickerFormat = this._formatService.dateFormat;
    this._costFormat = this._formatService.costFormatValue;
    this.getDateEnabledOption();
    this.LoadCycleCountChunks();
    if (this.loggedInSiteId) {
      this._LoadLocations(this.loggedInSiteId);
    }
  }

  _LoadLocations(siteId: number): void {
    const siteModel = { siteId: siteId };
    this._locationsService.getLocationsByAccounts(siteModel).subscribe((data: any) => {
      this._locations = data || [];
      this._locationsFiltered = data || [];
      if (this._cycleCountChunksDetails && this._cycleCountChunksDetails.length > 0) {
        this._populateLocationNames(this._cycleCountChunksDetails);
      }
    });
  }

  private _populateLocationNames(items: any[]): void {
    if (!items || items.length === 0) return;
    items.forEach((item: any) => {
      if (!item.expectedLocationName || item.expectedLocationName === '') {
        item.expectedLocationName = item.expectedLocationCode
          || this._locations?.find((l: any) => l.id === item.expectedLocationId)?.code
          || this._locations?.find((l: any) => l.id === item.expectedLocationId)?.name
          || item.expectedLocation?.code
          || item.expectedLocation?.description
          || '';
      }
      if (!item.actualLocationName || item.actualLocationName === '') {
        item.actualLocationName = item.actualLocationCode
          || this._locations?.find((l: any) => l.id === item.actualLocationId)?.code
          || this._locations?.find((l: any) => l.id === item.actualLocationId)?.name
          || item.actualLocation?.code
          || item.actualLocation?.description
          || '';
      }
    });
  }

  ngOnDestroy(): void {
    this._layoutManager.removeOverlay();
  }

  private _initForm(): void {
    this._form = this._fb.group({
      transactionDate: [this._sharedService.getTodayInUserTimezone(), Validators.required],
      cycleCountChunk: [null, Validators.required]
    });
  }

  showTextbox(): void {
    this.showTextBox = true;
  }

  closeTextbox(target: any): void {
    this.showTextBox = false;
    if (target?.value) {
      this.title = target.value;
    }
  }

  isLoading: boolean = false;
  hasLoadedVariance: boolean = false;

  _getSelectedChunk(): StockCycleCountChunkModel | null {
    const val = this._form?.controls['cycleCountChunk']?.value;
    if (!val) return null;
    if (typeof val === 'object' && val.id) {
      return val;
    }
    if (typeof val === 'number') {
      return this._cycleCountChunks.find(c => c.id === val) || null;
    }
    if (typeof val === 'string') {
      return this._cycleCountChunks.find(c => c.name?.toLowerCase() === val.toLowerCase()) || null;
    }
    return null;
  }

  get selectedChunk(): StockCycleCountChunkModel | null {
    return this._getSelectedChunk();
  }

  get selectedCount(): number {
    return this._cycleCountChunksDetails.filter((d) => d.adjusted).length;
  }

  get totalExpectedQuantity(): number {
    return this._cycleCountChunksDetails.reduce((sum, d) => sum + (d.expectedQuantity || 0), 0);
  }

  get totalActualQuantity(): number {
    return this._cycleCountChunksDetails.reduce((sum, d) => sum + (d.actualQuantity || 0), 0);
  }

  get totalVarianceQuantity(): number {
    return this._cycleCountChunksDetails.reduce((sum, d) => sum + (d.varianceQuantity || ((d.actualQuantity || 0) - (d.expectedQuantity || 0))), 0);
  }

  isAllSelected(): boolean {
    return this._cycleCountChunksDetails.length > 0 && this._cycleCountChunksDetails.every((d) => d.adjusted);
  }

  _onGridSelectionChange(keys: any[]): void {
    this._selectedKeys = keys || [];
    this._cycleCountChunksDetails.forEach((d) => {
      const id = d.id ?? this._cycleCountChunksDetails.indexOf(d);
      d.adjusted = this._selectedKeys.includes(id) || (d.id != null && this._selectedKeys.includes(d.id));
    });
    this._allSelected = this.isAllSelected();
  }

  toggleSelectAll(checked: boolean): void {
    this._allSelected = checked;
    this._cycleCountChunksDetails.forEach((d) => (d.adjusted = checked));
    this._selectedKeys = checked
      ? this._cycleCountChunksDetails.map((d, index) => d.id ?? index)
      : [];
  }

  _selectAdjustAll(): void {
    this._allSelected = !this._allSelected;
    this._cycleCountChunksDetails.forEach((d) => (d.adjusted = this._allSelected));
    this._selectedKeys = this._allSelected
      ? this._cycleCountChunksDetails.map((d, index) => d.id ?? index)
      : [];
  }

  onChunkChange(chunk: any): void {
    this.hasLoadedVariance = false;
    this._allSelected = false;
    this._selectedKeys = [];
    this._cycleCountChunksDetails = [];
    this._cycleCountChunksFiltered = [...this._cycleCountChunks];
  }

  _handleCycleCountChunksFilter(value: string): void {
    if (!value) {
      this._cycleCountChunksFiltered = [...this._cycleCountChunks];
    } else {
      this._cycleCountChunksFiltered = this._cycleCountChunks.filter(
        (s) => s.name && s.name.toLowerCase().indexOf(value.toLowerCase()) !== -1
      );
    }
  }

  LoadCycleCountChunks(): void {
    this.isLoading = true;
    this._stockCycleCountAdjustService.LoadCycleCountChunks().subscribe({
      next: (res: any) => {
        this.isLoading = false;
        if (res) {
          this._cycleCountChunks = Array.isArray(res) ? res : res?.data || [];
          this._cycleCountChunksFiltered = [...this._cycleCountChunks];
        }
      },
      error: () => {
        this.isLoading = false;
      }
    });
  }

  _LoadDetails(chunk: any): void {
    if (!chunk || !chunk.id) return;
    this.isLoading = true;
    this._stockCycleCountAdjustService.LoadCycleCountChunkVarianceDetails(chunk.id).subscribe({
      next: (data: any) => {
        this.isLoading = false;
        this.hasLoadedVariance = true;
        this._allSelected = false;
        this._selectedKeys = [];
        const items = Array.isArray(data) ? data : data?.data || [];
        this._cycleCountChunksDetails = items.map((item: any) => {
          const expectedLocName = item.expectedLocationName
            || item.expectedLocationCode
            || this._locations?.find((l: any) => l.id === item.expectedLocationId)?.code
            || this._locations?.find((l: any) => l.id === item.expectedLocationId)?.name
            || item.expectedLocation?.code
            || item.expectedLocation?.description
            || '';
          const actualLocName = item.actualLocationName
            || item.actualLocationCode
            || this._locations?.find((l: any) => l.id === item.actualLocationId)?.code
            || this._locations?.find((l: any) => l.id === item.actualLocationId)?.name
            || item.actualLocation?.code
            || item.actualLocation?.description
            || '';
          return {
            ...item,
            expectedLocationName: expectedLocName,
            actualLocationName: actualLocName,
            adjusted: false,
            varianceQuantity: (item.actualQuantity ?? 0) - (item.expectedQuantity ?? 0)
          };
        });
      },
      error: () => {
        this.isLoading = false;
      }
    });
  }

  getDateEnabledOption(): void {
    this._TransactionDateEditing.getTransactionDateEditing('get-data').subscribe((x: any) => {
      if (x != undefined && x != null) {
        this._dateEnabled = x.transactionDateEditing;
        if (!this._dateEnabled) {
          this._form.get('transactionDate')?.disable();
        }
      }
    });
  }

  _startDateSubmited(): void {
    setTimeout(() => {
      this.kendoStartDateTimePiker?.blur();
    });
  }

  _startDateOpened(): void {
    this._addClassActiveLocation = false;
    this._addClassActiveGrid = false;
  }

  _listboxClicked(): void {
    this._addClassActiveLocation = true;
    if (this.kendoStartDateTimePiker) {
      this.kendoStartDateTimePiker.toggle(false);
      this.kendoStartDateTimePiker.blur();
    }
    this._addClassActiveGrid = false;
    this._addClassActive3 = false;
  }

  _gridclicked(): void {
    this._addClassActiveGrid = true;
    this._addClassActiveLocation = false;
  }

  _step3Clicked(): void {
    this._addClassActive3 = true;
    if (this.kendoStartDateTimePiker) {
      this.kendoStartDateTimePiker.toggle(false);
      this.kendoStartDateTimePiker.blur();
    }
    this._addClassActiveGrid = false;
    this._addClassActiveLocation = false;
  }

  _showAllVariance(): void {
    if (this._form.invalid) {
      this._invalidForm = true;
      return;
    }
    const chunk = this._getSelectedChunk();
    if (!chunk || !chunk.id) {
      this._invalidForm = true;
      return;
    }
    this._LoadDetails(chunk);
  }

  _checkAll(): void {
    this._allSelected = true;
    this._cycleCountChunksDetails.forEach((d) => (d.adjusted = true));
    this._selectedKeys = this._cycleCountChunksDetails.map((d, index) => d.id ?? index);
  }

  _uncheckAll(): void {
    this._allSelected = false;
    this._cycleCountChunksDetails.forEach((d) => (d.adjusted = false));
    this._selectedKeys = [];
  }

  showCheckAll(): boolean {
    return this._cycleCountChunksDetails.every((d) => !d.adjusted);
  }

  onAdjustedChange(item: StockCycleCountChunkDetailModel): void {
    item.adjusted = !item.adjusted;
    const itemId = item.id ?? this._cycleCountChunksDetails.indexOf(item);
    if (item.adjusted) {
      if (!this._selectedKeys.includes(itemId)) {
        this._selectedKeys = [...this._selectedKeys, itemId];
      }
    } else {
      this._selectedKeys = this._selectedKeys.filter((k) => k !== itemId);
    }
    this._allSelected = this.isAllSelected();
  }

  _process(): void {
    if (this._form.invalid) {
      this._invalidForm = true;
      return;
    }

    const chunkVal = this._getSelectedChunk();
    if (!chunkVal || !chunkVal.id) {
      this.setfinalyPupmode('Please select a valid cycle count chunk.');
      return;
    }

    const selectedDetails = this._cycleCountChunksDetails.filter((d) => d.adjusted || this._selectedKeys.includes(d.id));
    if (selectedDetails.length === 0) {
      this.setfinalyPupmode('Please select at least one item to adjust.');
      return;
    }

    const model: StockAdjustCycleCountDetailsModel = {
      chunckId: chunkVal.id,
      stockCycleCountChunkDetails: selectedDetails,
      transactionDate: this._form.controls['transactionDate'].value,
      closeAllChuncks: false
    };

    this.isLoading = true;
    this._stockCycleCountAdjustService.AdjustProcess(model).subscribe({
      next: (res: any) => {
        this.isLoading = false;
        const msg = res?.data?.message || res?.message || 'Cycle count adjustments processed successfully.';
        this._onClear();
        this.setfinalyPupmode(msg);
      },
      error: (error: any) => {
        this.isLoading = false;
        const msg = error?.message || 'Error occurred';
        this.setfinalyPupmode(msg);
      }
    });
  }

  _processAndClose(): void {
    if (this._form.invalid) {
      this._invalidForm = true;
      return;
    }

    const chunkVal = this._getSelectedChunk();
    if (!chunkVal || !chunkVal.id) {
      this.setfinalyPupmode('Please select a valid cycle count chunk.');
      return;
    }

    const selectedDetails = this._cycleCountChunksDetails.filter((d) => d.adjusted || this._selectedKeys.includes(d.id));
    if (selectedDetails.length === 0) {
      this.setfinalyPupmode('Please select at least one item to adjust.');
      return;
    }

    const model: StockAdjustCycleCountDetailsModel = {
      chunckId: chunkVal.id,
      stockCycleCountChunkDetails: selectedDetails,
      transactionDate: this._form.controls['transactionDate'].value,
      closeAllChuncks: true
    };

    this.isLoading = true;
    this._stockCycleCountAdjustService.AdjustProcess(model).subscribe({
      next: (res: any) => {
        this.isLoading = false;
        const msg = res?.data?.message || res?.message || 'Cycle count adjustments processed and closed successfully.';
        this._onClear();
        this.setfinalyPupmode(msg);
      },
      error: (error: any) => {
        this.isLoading = false;
        const msg = error?.message || 'Error occurred';
        this.setfinalyPupmode(msg);
      }
    });
  }

  private setfinalyPupmode(msg: any): void {
    this._layoutComponent.openPopup({
      title: 'Alert',
      message: msg,
      withoutConfirm: true
    });
  }

  _onClear(): void {
    this._invalidForm = false;
    this.hasLoadedVariance = false;
    this._allSelected = false;
    this._selectedKeys = [];
    this._cycleCountChunksDetails = [];
    this._cycleCountChunksFiltered = [];
    this._form.reset();
    this._initForm();
    this.getDateEnabledOption();
    this.LoadCycleCountChunks();
    if (this.loggedInSiteId && (!this._locations || this._locations.length === 0)) {
      this._LoadLocations(this.loggedInSiteId);
    }
  }

  _reset(): void {
    this._onClear();
  }

  _export(): void {
    this.grid?.saveAsPDF();
  }

  _refresh(): void {
    this._onClear();
  }
}
